<template>
  <button
      @click="isOpen = true"
      v-bind="$attrs"
      class="lg:bg-black flex items-center space-x-2 border border-gray-900/10
      shadow-sm lg:mt-3 lg:mt-0 px-3 py:3 lg:py-1.5 lg:hover:border-gray-300 focus:outline-none
       lg:focus:border-gray-300 lg:rounded-lg">
    <svg class="flex-none text-gray-400 -ml-1" width="24" height="24" fill="none" aria-hidden="true">
      <path d="m19 19-3.5-3.5" stroke="currentColor" stroke-width="2" stroke-linecap="round"
            stroke-linejoin="round"></path>
      <circle cx="11" cy="11" r="6" stroke="currentColor" stroke-width="2" stroke-linecap="round"
              stroke-linejoin="round"></circle>
    </svg>
    <span class="text-white hidden lg:block lg:text-sm lg:text-gray-400 lg:flex-1 lg:text-left">Search...</span>
    <span class="flex-none hidden lg:block text-xs font-semibold text-gray-400">{{ keyboardShortcut }}</span>
  </button>

  <TransitionRoot :show="isOpen" as="template">
    <Dialog
        class="fixed inset-0 z-50 flex justify-center items-start"
        :open="isOpen"
        @close="isOpen = false"
        @keydown.up.prevent="handleKeyNavigation"
        @keydown.down.prevent="handleKeyNavigation">
      <TransitionChild
          enter="duration-200 ease-out"
          enter-from="opacity-0"
          enter-to="opacity-100"
          leave="duration-200 ease-in"
          leave-from="opacity-100"
          leave-to="opacity-0"
          as="template">
        <DialogOverlay
            class="fixed inset-0 bg-black bg-opacity-70"
        ></DialogOverlay>
      </TransitionChild>

      <TransitionChild
          enter="duration-200 ease-out"
          enter-from="opacity-0 scale-95"
          enter-to="opacity-100 scale-100"
          leave="duration-200 ease-in"
          leave-from="opacity-100 scale-100"
          leave-to="opacity-0 scale-95"
          as="template">
        <div
            class="flex flex-col overflow-hidden z-0 w-full max-w-2xl bg-white rounded-lg mx-4 max-h-[80vh] mt-[10vh] relative"
        >
          <form
              @submit.prevent="onSubmit"
              class="relative flex items-center"
              action="#">
            <div class="absolute inset-y-0 left-0 flex items-center pl-4 pointer-events-none">
              <svg
                  class="w-5 h-5 text-gray-700"
                  xmlns="http://www.w3.org/2000/svg" fill="none"
                  viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
              </svg>
            </div>

            <input
                ref="searchInput"
                @input="(e) => search(e.target.value)"
                class="w-full py-4 pl-12 border-b border-gray-100 outline-none placeholder-gray-400"
                type="text" placeholder="Search...">

            <div class="absolute inset-y-0 right-0 flex items-center pr-3">
              <button
                  @click="isOpen = false"
                  class="flex items-center p-1.5 uppercase font-semibold tracking-wider text-gray-700 rounded-md border border-gray-200 focus:outline-none focus:border-gray-300 text-xxs"
                  type="button">Esc
              </button>
            </div>
          </form>

          <div class="overflow-auto">


              <ul v-if="presenters.length > 0"
                  class="divide-y divide-gray-100">
                <li v-for="(presenter, index) in presenters"
                    :key="'p-' + index"
                    :ref="el => { presenterRefs[index] = el }"
                    :class="selectedIndex === `p-${index}` ? 'bg-gray-100' : ''"
                    @mousemove="selectedIndex = `p-${index}`"
                    class="flex items-center px-4 py-2.5 relative">
                  <a :href="'/person/' + presenter.id">
                    <span class="absolute inset-0"></span>
                  </a>
                  <img :src="userImage(presenter)" alt=""
                       class="w-16 h-16 rounded-full object-cover border-white border-2 shrink-0 bg-gray-200"
                  >
                  <div class="ml-3">
                    <div class="font-semibold text-gray-600">
                      {{ presenter.name || '-' }}
                    </div>
                    <div class="text-xs text-gray-600 mt-1">
                      Presenter
                    </div>
                  </div>
                </li>
              </ul>


            <ul v-if="sessions.length > 0"
                class="divide-y divide-gray-100"
            >
              <li
                  v-for="(item, index) in sessions"
                  :key="'s-' + index"
                  :ref="el => { resultsRefs[index] = el }"
                  :class="selectedIndex === `s-${index}` ? 'bg-gray-100' : ''"
                  @mousemove="selectedIndex = `s-${index}`"
                  class="flex items-center px-4 py-2.5 relative"
              >
                <img :src="sessionLeadImage(item)" alt=""
                     class="w-16 h-16 rounded-full object-cover border-white border-2 shrink-0 bg-gray-200"
                >
                <a :href="'/session/' + item.id">
                  <span class="absolute inset-0"></span>
                </a>
                <div class="ml-3">
                  <div class="font-semibold text-gray-600">
                    {{ item.title }}
                  </div>
                  <div class="text-xs text-gray-600 mt-1">
                    {{ item.category }}
                  </div>
                </div>
              </li>
            </ul>

            <p v-if="sessions.length === 0 && presenters.length === 0" class="p-10 text-lg text-center text-gray-400">
              No results...
            </p>
          </div>
        </div>
      </TransitionChild>
    </Dialog>
  </TransitionRoot>
</template>

<script>
import {Dialog, DialogOverlay, TransitionChild, TransitionRoot} from "@headlessui/vue";
import { ref, computed, nextTick, onMounted, onUnmounted } from 'vue';
import axios from "axios";
import {debounce} from "lodash";

export default {
  components: {Dialog, DialogOverlay, TransitionRoot, TransitionChild},
  setup() {
    const isAppleOS = () => {
      const platform = navigator?.userAgentData?.platform || navigator?.platform || "unknown";
      return /(Mac|iPhone|iPod|iPad)/i.test(platform);
    }

    onMounted(() => {
      window.addEventListener('keydown', onKeyDown);
      nextTick(() => {
        searchInput.value?.focus();
      });
    });

    console.log('loaded');

    const keyboardShortcut = isAppleOS() ? "⌘K" : "Ctrl+K";
    const isOpen = ref(false);

    const onKeyDown = (event) => {
      if (isOpen.value) return;

      if ((event.metaKey || event.ctrlKey) && event.key === 'k') {
        isOpen.value = true;
      }
    };

    const sessions = ref([]);
    const presenters = ref([]);
    const resultsRefs = ref([]);
    const presenterRefs = ref([]);
    const selectedIndex = ref('');
    const searchInput = ref(null);

    const search = debounce(async (term) => {
      let {data} = await axios.get("/search/new", {params: {term: term}});
      //results.value = data;
      sessions.value = data.sessions || [];
      presenters.value = data.presenters || [];

      //console.log(results);
      resultsRefs.value = [];
      presenterRefs.value = [];
      await nextTick();

    }, 250);

    const navigationItems = computed(() => {
      return [
        ...presenters.value.map((_, i) => `p-${i}`),
        ...sessions.value.map((_, i) => `s-${i}`)
      ];
    });

    const handleKeyNavigation = (event) => {
      const nav = navigationItems.value;
      if (nav.length === 0) return;

      const current = nav.indexOf(selectedIndex.value);
      let nextIndex = current;

      if (event.key === 'ArrowDown') {
        event.preventDefault();
        nextIndex = current === -1 ? 0 : (current + 1) % nav.length;
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        nextIndex = current === -1 ? nav.length - 1 : (current - 1 + nav.length) % nav.length;
      } else {
        return; // Do nothing for other keys
      }

      selectedIndex.value = nav[nextIndex];

      const [prefix, index] = selectedIndex.value.split("-");
      const idx = Number(index);

      if (prefix === "s") {
        resultsRefs.value[idx]?.scrollIntoView({ block: "nearest" });
      } else if (prefix === "p") {
        presenterRefs.value[idx]?.scrollIntoView({ block: "nearest" });
      }
    };

    const onTermKeydown = (event) => {
      if (['ArrowUp', 'ArrowDown'].includes(event.code)) {
        event.preventDefault();
      }
    }

    const onSubmit = () => {
      let target = selectedIndex.value;

      if (!target) {
        if (presenters.value.length > 0) {
          target = 'p-0';
        } else if (sessions.value.length > 0) {
          target = 's-0';
        } else {
          return; // No results, do nothing
        }
      }

      const [prefix, index] = target.split('-');
      const idx = Number(index);

      if (prefix === 's') {
        const item = sessions.value[idx];
        if (item?.url) {
          window.location = item.url;
        }
      } else if (prefix === 'p') {
        const presenter = presenters.value[idx];
        if (presenter?.id) {
          window.location = `/person/${presenter.id}`;
        }
      }
    };

    onMounted(() => {
      window.addEventListener('keydown', onKeyDown);
      search();
    });
    onUnmounted(() => window.removeEventListener('keydown', onKeyDown));

    function sessionLeadImage(session) {
      if (session?.participants?.length) {
        const lead = session.participants[0];
        if (lead?.member?.avatar_path) {
          return lead?.member?.avatar_path;
        }
      }
      return '/img/user-astronaut-square.png';
    }

    function userImage(member) {
      if (member?.avatar_path) {
        return member?.avatar_path;
      }
      return '/img/user-astronaut-square.png';
    }

    return {
      sessionLeadImage,
      userImage,
      onSubmit,
      handleKeyNavigation,
      onTermKeydown,
      presenterRefs,
      resultsRefs,
      selectedIndex,
      sessions,
      presenters,
      search,
      isOpen,
      keyboardShortcut
    }
  },
}
</script>
